import { Search } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { moduleRank } from './module-order';
import { useRoleText } from './role-text';
import type { AssignableRole } from './use-assignable-roles';

/** Name contains, ignoring case and accents. */
function fold(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase();
}

function RoleSearch({
  query,
  onQuery,
}: {
  readonly query: string;
  readonly onQuery: (value: string) => void;
}) {
  const { t } = useTranslation('administration');
  const searchId = useId();
  return (
    <div className="relative">
      <label htmlFor={searchId} className="sr-only">
        {t('roleSearch.label')}
      </label>
      <Search
        aria-hidden="true"
        className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
      />
      <Input
        id={searchId}
        type="search"
        value={query}
        onChange={(event) => onQuery(event.target.value)}
        placeholder={t('roleSearch.placeholder')}
        className="pl-9"
      />
    </div>
  );
}

function RoleOption({
  role,
  note,
  selected,
  onToggle,
}: {
  readonly role: AssignableRole;
  readonly note: string | undefined;
  readonly selected: boolean;
  readonly onToggle: (roleId: string) => void;
}) {
  const { name, description } = useRoleText();
  const checked = note !== undefined || selected;
  const desc = description(role);
  return (
    <label
      className={cn(
        'role-option',
        checked && 'role-option-checked',
        note !== undefined && 'role-option-held',
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={note !== undefined}
        onChange={() => onToggle(role.id)}
      />
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="role-option-name">{name(role)}</span>
        {desc !== null ? <span className="role-option-desc">{desc}</span> : null}
        {note !== undefined ? <span className="role-option-note">{note}</span> : null}
      </span>
    </label>
  );
}

/**
 * The role picker of Invita and of the grant panels (FR-AD-5): only the roles this caller may
 * assign here, each named and described in plain words, grouped by module, with a search over
 * names. A role the person already holds can be shown checked and disabled, with a note.
 */
export function RoleChecklist({
  roles,
  selected,
  onToggle,
  held = new Map(),
  legend,
}: {
  readonly roles: readonly AssignableRole[];
  readonly selected: ReadonlySet<string>;
  readonly onToggle: (roleId: string) => void;
  /** Roles already held: a note in place of the choice, and no checkbox to tick. */
  readonly held?: ReadonlyMap<string, string>;
  readonly legend: string;
}) {
  const { t } = useTranslation('administration');
  const { name } = useRoleText();
  const [query, setQuery] = useState('');

  const groups = useMemo(() => {
    const byModule = new Map<string, AssignableRole[]>();
    for (const role of roles) {
      if (query !== '' && !fold(name(role)).includes(fold(query))) {
        continue;
      }
      const key = role.module?.code ?? 'administration';
      byModule.set(key, [...(byModule.get(key) ?? []), role]);
    }
    return [...byModule.entries()].sort(([a], [b]) => moduleRank(a) - moduleRank(b));
  }, [roles, query, name]);

  const moduleLabel = (code: string) =>
    t(`modules.${code}` as 'modules.administration', {
      defaultValue: roles.find((role) => role.module?.code === code)?.module?.name ?? code,
    });

  return (
    <fieldset className="role-checklist">
      <legend className="sr-only">{legend}</legend>
      <RoleSearch query={query} onQuery={setQuery} />
      {groups.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('roleSearch.empty', { query })}</p>
      ) : null}
      {groups.map(([code, items]) => (
        <div
          key={code}
          role="group"
          aria-label={moduleLabel(code)}
          className="flex flex-col gap-1.5"
        >
          <p className="role-checklist-group" aria-hidden="true">
            {moduleLabel(code)}
          </p>
          {items.map((role) => (
            <RoleOption
              key={role.id}
              role={role}
              note={held.get(role.id)}
              selected={selected.has(role.id)}
              onToggle={onToggle}
            />
          ))}
        </div>
      ))}
    </fieldset>
  );
}
